import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supa, BACKEND_URL } from "../config/supabase.js";
import { api } from "../config/api.js";
import { simpleHash } from "../utils/helpers.js";
import { Icon, BloomMark, Wordmark } from "../components/Icon.jsx";

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export function Auth({ mode, setPage, setSessionAndLoad }) {
  const navigate = useNavigate();
  const [form, setForm]     = useState({name:"",email:"",store:"",instagram:"",pass:"",pass2:""});
  const [errs, setErrs]     = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep]     = useState("form"); // "form" | "otp" | "pending"
  const [otpVal, setOtpVal] = useState("");
  const [otpErr, setOtpErr] = useState("");
  const [igStatus, setIgStatus] = useState("idle"); // "idle"|"checking"|"ok"|"fail"|"unknown"
  const [pendingUser, setPendingUser] = useState(null);
  const h = e => setForm(f=>({...f,[e.target.name]:e.target.value}));
  const Err = ({k})=>errs[k]?<p style={{fontSize:11,color:"var(--red)",marginTop:4}}>{errs[k]}</p>:null;

  const cleanIg = (raw) => raw.trim()
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, "") // strip https://instagram.com/ or https://www.instagram.com/
    .replace(/^(www\.)?instagram\.com\//i, "")            // strip instagram.com/ or www.instagram.com/ (no protocol)
    .replace(/^@/, "")                                     // strip leading @
    .replace(/[/?#].*$/, "")                               // strip anything after first / ? or #
    .trim();

  const checkIg = async (handle) => {
    if (!handle) return;
    setIgStatus("checking");
    try {
      const r = await fetch(`${BACKEND_URL}/api/check-instagram/${encodeURIComponent(handle)}`);
      const d = await r.json();
      setIgStatus(d.exists === true ? "ok" : d.exists === false ? "fail" : "unknown");
    } catch { setIgStatus("unknown"); }
  };

  const submit = async () => {
    const e = {};
    if (mode==="signup") {
      if (!form.name.trim())  e.name  = "Name is required";
      if (!form.store.trim()) e.store = "Store name is required";
      const igRaw = cleanIg(form.instagram);
      if (!igRaw) e.instagram = "Instagram handle is required";
      if (igStatus === "fail") e.instagram = "This Instagram account doesn't exist — please check the handle";
      if (form.pass!==form.pass2) e.pass2 = "Passwords don't match";
    }
    if (!form.email.includes("@")) e.email = "Enter a valid email";
    if (form.pass.length<6) e.pass = "Password must be 6+ characters";
    if (Object.keys(e).length) { setErrs(e); return; }

    setLoading(true);
    try {
      if (mode==="signup") {
        // Check email not taken (server-side — the anon key no longer reads bloom_users)
        const chk = await fetch(`${BACKEND_URL}/api/auth/check-email?email=${encodeURIComponent(form.email.toLowerCase().trim())}`).then(r=>r.ok?r.json():{exists:false}).catch(()=>({exists:false}));
        if (chk.exists) { setErrs({email:"Email already registered"}); setLoading(false); return; }
        // Send OTP
        const otpRes = await fetch(`${BACKEND_URL}/api/auth/send-otp`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.toLowerCase().trim()})});
        const otpData = await otpRes.json();
        if (!otpRes.ok) { setErrs({email:otpData.error||"Failed to send verification email"}); setLoading(false); return; }
        setLoading(false);
        setStep("otp");
      } else {
        // Login — password is verified SERVER-SIDE; the browser never reads password_hash.
        const r = await fetch(`${BACKEND_URL}/api/auth/login`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.toLowerCase().trim(),password:form.pass})});
        const d = await r.json().catch(()=>({}));
        if (r.status===403 && d.error==="pending_approval") {
          setPendingUser({ store_name: form.store, email: form.email.toLowerCase().trim(), instagram: "" });
          setStep("pending"); setLoading(false); return;
        }
        if (!r.ok) { setErrs({pass: d.error==="Invalid email or password" ? "Wrong email or password" : (d.error||"Login failed")}); setLoading(false); return; }
        await setSessionAndLoad({ userId:d.user.id, role:"business", token:d.token, user:d.user });
      }
    } catch(err) {
      setErrs({email:"Something went wrong. Check your connection."}); setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otpVal.trim().length !== 6) { setOtpErr("Enter the 6-digit code"); return; }
    setLoading(true); setOtpErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/auth/verify-otp`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.toLowerCase().trim(),otp:otpVal.trim()})});
      const d = await r.json();
      if (!r.ok) { setOtpErr(d.error||"Wrong code"); setLoading(false); return; }
      // OTP verified — create account
      const igClean = cleanIg(form.instagram);
      const slug = form.store.toLowerCase().replace(/[^a-z0-9]/g,"-").replace(/-+/g,"-");
      const newUser = {
        id:`u-${Date.now()}`, name:form.name, email:form.email.toLowerCase().trim(),
        password_hash:simpleHash(form.pass), store_name:form.store,
        store_slug:slug, upi_id:"", store_desc:"", mailersend_key:"",
        instagram:igClean,
      };
      const saved = await api.insertUser(newUser); // throws with the real reason on failure
      setPendingUser(saved);
      setLoading(false);
      setStep("pending");
    } catch(err) { setOtpErr(err?.message || "Something went wrong. Try again."); setLoading(false); }
  };

  const resendOtp = async () => {
    setOtpErr(""); setLoading(true);
    await fetch(`${BACKEND_URL}/api/auth/send-otp`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.toLowerCase().trim()})});
    setLoading(false); setOtpErr("New code sent ✓");
  };

  const bg = "var(--offwhite)";

  // ── PENDING SCREEN ────────────────────────────────────────────────────────────
  if (step==="pending") return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:bg,padding:"40px 16px"}}>
      <div className="card fadeUp" style={{width:"100%",maxWidth:440,padding:44,textAlign:"center"}}>
        <div style={{fontSize:52,marginBottom:16}}><Icon n="pending" size={52} inline /></div>
        <h2 style={{fontSize:26,marginBottom:8}}>Store under review</h2>
        <p style={{color:"var(--ink-soft)",fontSize:14,lineHeight:1.7,marginBottom:24}}>
          Your store <strong>{pendingUser?.store_name||form.store}</strong> is pending approval.<br/>
          We'll verify your Instagram <strong>@{cleanIg(form.instagram)||pendingUser?.instagram}</strong> and email you once it's live — usually within 24 hours.
        </p>
        <div style={{background:"var(--surface)",borderRadius:4,padding:16,fontSize:13,color:"var(--ink-soft)",marginBottom:20}}>
          <Icon n="email" size="1.1em" inline /> Approval notification will be sent to <strong>{form.email||pendingUser?.email}</strong>
        </div>
        <button className="btn-ghost" style={{width:"100%",padding:13,borderRadius:4}} onClick={()=>navigate("/")}>← Back to Bloom</button>
      </div>
    </div>
  );

  // ── OTP SCREEN ────────────────────────────────────────────────────────────────
  if (step==="otp") return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:bg,padding:"40px 16px"}}>
      {loading&&<div style={{position:"fixed",inset:0,background:"var(--overlay)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}}><div style={{width:48,height:48,border:"3px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/></div>}
      <div className="card fadeUp" style={{width:"100%",maxWidth:400,padding:44,textAlign:"center"}}>
        <div style={{marginBottom:16}}><Wordmark size={36}/></div>
        <h2 style={{fontSize:24,marginBottom:8}}>Verify your email</h2>
        <p style={{color:"var(--ink-soft)",fontSize:13,marginBottom:24}}>We sent a 6-digit code to<br/><strong>{form.email}</strong></p>
        <input
          className="inp" value={otpVal} onChange={e=>setOtpVal(e.target.value.replace(/\D/g,"").slice(0,6))}
          placeholder="000000" maxLength={6}
          style={{textAlign:"center",fontSize:28,fontWeight:700,letterSpacing:8,padding:"14px 20px",borderRadius:4,marginBottom:8}}
          onKeyDown={e=>e.key==="Enter"&&verifyOtp()}
          autoFocus
        />
        {otpErr&&<p style={{fontSize:12,color:otpErr.includes("✓")?"var(--teal)":"var(--red)",marginBottom:8}}>{otpErr}</p>}
        <button className="btn-pink" style={{width:"100%",padding:14,borderRadius:4,marginTop:8,fontSize:14}} onClick={verifyOtp} disabled={loading}>
          {loading?"Verifying…":"Verify & Continue →"}
        </button>
        <p style={{fontSize:12,color:"var(--ink-soft)",marginTop:16}}>
          Didn't get it? <span style={{color:"var(--pink-btn)",cursor:"pointer",fontWeight:600}} onClick={resendOtp}>Resend code</span>
          {" · "}<span style={{color:"var(--ink-soft)",cursor:"pointer"}} onClick={()=>setStep("form")}>← Change email</span>
        </p>
      </div>
    </div>
  );

  // ── MAIN FORM ─────────────────────────────────────────────────────────────────
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:bg,padding:"40px 16px"}}>
      {loading&&(
        <div style={{position:"fixed",inset:0,background:"var(--overlay)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:9999,gap:16}}>
          <div style={{width:48,height:48,border:"3px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
          <p style={{color:"#fff",fontFamily:"Syne",fontWeight:600,fontSize:16}}>{mode==="login"?"Logging you in…":"Sending verification code…"}</p>
        </div>
      )}
      <div className="card fadeUp" style={{width:"100%",maxWidth:440,padding:44}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{marginBottom:10}}><Wordmark size={44}/></div>
          <h2 style={{fontSize:28}}>{mode==="login"?"Welcome back":"Start for free"}</h2>
          <p style={{color:"var(--ink-soft)",marginTop:6,fontSize:13}}>{mode==="login"?"Log in to your Bloom dashboard":"No credit card. No KYC. Ever."}</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:13}}>
          {mode==="signup"&&<div><input className={`inp${errs.name?" err":""}`} name="name" placeholder="Full name" value={form.name} onChange={h}/><Err k="name"/></div>}
          <div><input className={`inp${errs.email?" err":""}`} name="email" type="email" placeholder="Email address" value={form.email} onChange={h}/><Err k="email"/></div>
          {mode==="signup"&&<div><input className={`inp${errs.store?" err":""}`} name="store" placeholder="Store name (e.g. Bloom by Priya)" value={form.store} onChange={h}/><Err k="store"/></div>}
          {mode==="signup"&&(
            <div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:13,color:"var(--ink-soft)",fontWeight:700,pointerEvents:"none"}}>@</span>
                <input className={`inp${errs.instagram?" err":""}`} name="instagram" placeholder="Instagram handle (required)" value={form.instagram} onChange={h}
                  onBlur={()=>{ const ig=cleanIg(form.instagram); if(ig) checkIg(ig); }}
                  autoComplete="off"
                  style={{paddingLeft:30,paddingRight:36}}/>
                <span style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:14}}>
                  {igStatus==="checking"?<><Icon n="pending" size="1.1em" inline /></>:igStatus==="ok"?<><Icon n="success" size="1.1em" inline /></>:igStatus==="fail"?<><Icon n="close" size="1.1em" inline /></>:""}
                </span>
              </div>
              <Err k="instagram"/>
              {igStatus==="ok"&&<p style={{fontSize:11,color:"var(--teal)",marginTop:4,paddingLeft:4}}><Icon n="done" size="1.1em" inline /> Instagram account found</p>}
              {igStatus==="fail"&&<p style={{fontSize:11,color:"var(--red)",marginTop:4,paddingLeft:4}}>Account not found — double-check your handle</p>}
              {igStatus==="idle"&&<p style={{fontSize:11,color:"var(--ink-soft)",marginTop:4,paddingLeft:4}}>We verify all stores via Instagram before activation</p>}
            </div>
          )}
          <div><input className={`inp${errs.pass?" err":""}`} name="pass" type="password" placeholder="Password (6+ characters)" value={form.pass} onChange={h}/><Err k="pass"/></div>
          {mode==="signup"&&<div><input className={`inp${errs.pass2?" err":""}`} name="pass2" type="password" placeholder="Confirm password" value={form.pass2} onChange={h}/><Err k="pass2"/></div>}
          <button className="btn-pink" style={{marginTop:4,padding:14,fontSize:14,borderRadius:4}} onClick={submit} disabled={loading}>
            {loading?(mode==="login"?"Logging in…":"Sending code…"):mode==="login"?"Log In":<>{"Create My Store "}<BloomMark /></>}
          </button>
          {mode==="login"&&(
            <p style={{textAlign:"center",fontSize:12,color:"var(--ink-soft)",marginTop:4}}>
              <span style={{color:"var(--teal)",cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/forgot-password")}>Forgot password?</span>
            </p>
          )}
        </div>
        <p style={{textAlign:"center",marginTop:18,fontSize:13,color:"var(--ink-soft)"}}>
          {mode==="login"?"New here? ":"Already have a store? "}
          <span style={{color:"var(--pink-btn)",cursor:"pointer",fontWeight:600}} onClick={()=>{setErrs({});setStep("form");setPage(mode==="login"?"signup":"login");}}>
            {mode==="login"?"Create free store":"Log in"}
          </span>
        </p>
        <div style={{marginTop:20,padding:14,background:"var(--surface)",borderRadius:4,textAlign:"center"}}>
          <p style={{fontSize:13,color:"var(--ink-soft)",lineHeight:1.7}}>
            New to Bloom? <span style={{color:"var(--pink-btn)",fontWeight:700,cursor:"pointer"}} onClick={()=>navigate("/directory")}>Browse Stores →</span>
          </p>
        </div>
      </div>
    </div>
  );
}
